
import {
	ArrayFilter,
	ComparableType,
	restoreFilters,
	saveFilters,
	STRING_COMPARISON_OPERATORS,
	useArrayFilters,
} from "@/hooks/useArrayFilters";
import { useResultsSorting } from "@/hooks/useResultsSorting";
import { TableColumn, useTableColumns } from "@/hooks/useTableColumns";
import { Result, watcherService } from "@/services/watcherService";
import { TableColumnFilterOptions } from "@/types";
import { localStorageGetBoolean } from "@/utils";
import { exportTable } from "@/utils/export";
import { computed, defineComponent, ref, watch } from "vue";
import ColumnFilterDropdown, {
	NewArrayFilter,
} from "./ColumnFilterDropdown.vue";
import MultipleSelectDropdown from "./MultipleSelectDropdown.vue";
import ResultRow from "./ResultRow.vue";
import SortingToggle from "./SortingToggle.vue";

export default defineComponent({
	components: {
		ResultRow,
		MultipleSelectDropdown,
		ColumnFilterDropdown,
		SortingToggle,
	},
	setup() {
		const { results } = watcherService;

		// whether to show the full domain of the URL or just a pathname
		const showFullDomain = ref(
			localStorageGetBoolean("resultsShowFullDomain", false)
		);
		watch(showFullDomain, (newValue: boolean) => {
			localStorage["resultsShowFullDomain"] = newValue;
		});

		// toggleable table columns
		const initialColumns: (TableColumn & TableColumnFilterOptions)[] = [
			{ value: "sitemap", title: "Sitemap", enabled: true },
			{
				value: "robots",
				title: "Robots",
				enabled: true,
				filteringEnabled: true,
				comparisonOperators: STRING_COMPARISON_OPERATORS,
			},
			{
				value: "ads",
				title: "Ads",
				enabled: true,
				filteringEnabled: true,
				comparisonOperators: STRING_COMPARISON_OPERATORS,
			},
			{
				value: "status",
				title: "Status",
				enabled: true,
				filteringEnabled: true,
			},
			{
				value: "duration",
				title: "Duration",
				enabled: true,
				filteringEnabled: true,
			},
			{ value: "url", title: "URL", enabled: true },
			{ value: "caption", title: "Caption", enabled: true },
			{ value: "title", title: "Title", enabled: true },
			{
				value: "description-length",
				title: "Description Length",
				enabled: true,
				filteringEnabled: true,
			},
		];
		const { columns, isColumnEnabled, toggleColumn, columnsBitmask } =
			useTableColumns(initialColumns);

		// result filters
		const filterOptions = initialColumns.filter(col =>
			Boolean(col.filteringEnabled)
		);
		const {
			filters,
			addFilter,
			deleteFilter,
			applyFilters,
			setFilterEnabled,
		} = useArrayFilters();

		// override filters getters for custom fields
		const columnGetterOverrides: Record<string, ArrayFilter["getter"]> = {
			ads: (result: Result) => (result.ads ? "yes" : "no"),
			duration: (result: Result) => result.durationMS / 1000,
			"description-length": (result: Result) =>
				result.description ? result.description.length : "",
		};
		const getGetterForKey = (key: string) =>
			columnGetterOverrides[key] ||
			((result: Result) => {
				const value = result[
					key as keyof Result
				] as ComparableType | null;
				return value === null ? "" : value;
			});
		const handleAddFilter = (newFilter: NewArrayFilter) => {
			const getter = getGetterForKey(newFilter.key);
			addFilter({
				...newFilter,
				getter,
				enabled: newFilter.enabled ?? true,
			});
		};

		// save/restore filters from localStorage
		for (const filter of restoreFilters("resultsFilters")) {
			handleAddFilter(filter);
		}
		watch(filters.value, (newFilters: ArrayFilter[]) => {
			saveFilters(newFilters, "resultsFilters");
		});

		// filter results
		const filteredResults = computed(() =>
			applyFilters(results.value, filters.value)
		);

		// sort results
		const { setSortingOrder, isAscendingColumn, applySorting } =
			useResultsSorting<Result>(columnGetterOverrides);
		const sortedResults = computed(() =>
			applySorting(filteredResults.value)
		);

		const handleExport = () => {
			exportTable(
				columns.value,
				sortedResults.value,
				columnGetterOverrides
			);
		};

		return {
			results: sortedResults,

			showFullDomain,

			columns,
			columnsBitmask,
			isColumnEnabled,
			toggleColumn,

			filters,
			filterOptions,
			handleAddFilter,
			deleteFilter,
			setFilterEnabled,

			setSortingOrder,
			isAscendingColumn,

			handleExport,
		};
	},
});
